import { useState, useContext, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Modal,
  Fade,
  Backdrop,
  Card,
  Icon,
  Tooltip,
  Tab,
  Tabs,
  CircularProgress,
} from "@mui/material";
import PropTypes from "prop-types";
// Kaizen Dashboard components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import { MessageManager } from "context";
import { AdministrarContext } from "./context";
import { gql, useLazyQuery, useMutation } from "@apollo/client";

//tabs
import Tab0 from "./tab0";
import Tab1 from "./tab1";
import Tab2 from "./tab2";
import Tab4 from "./tab4";
import { ConfirmarContext } from "context/ConfirmarContext";
import { formatDate, formatMoneyPunto } from "utils/formatters";
import { getUproColor } from "utils/colors";
import { UserContext } from "context/user";
import { MenuContext } from "context/menuContext";
import { nombreCompraDetalle } from "utils/presentacionCompra";
import { PERMISOS_COMPRAS, PERMISOS_CREAR_SOLICITUD } from "layouts/compras/permisos";
import {
  TIPOS_COMPRA,
  validarPedido,
  calcularTotalEstimado,
  verificarTipoCompraSegunMonto,
  describirIntervalo,
  puedeEditarDatosGenerales,
  puedeCambiarTipoCompra,
} from "./reglasPedido";

// Campos de PedidoCompraInput que el modal envía. Los que asigna el servidor
// (expediente, estado, auditoría, sucursal, domicilio y firmante) no se envían.
const CAMPOS_PEDIDO_EDITABLES = [
  "nombre", "tipoCompra", "tipoDetalle",
  "stockObservaciones", "stockEstado", "stockUser", "stockFecha",
  "detalles", "idResponsable",
  "objetoCompra", "justificacion", "fechaSolicitud",
  "motivoAnulacion", "fechaEntrega",
  "pliegoVencimientoDias", "pliegoRequiereDemanda", "pliegoParticularidades",
];

// Claves de primer nivel que cambiaron entre dos versiones del pedido.
const obtenerCambios = (anterior, siguiente) => {
  const cambios = {};
  Object.keys(siguiente || {}).forEach((key) => {
    if (siguiente[key] !== anterior?.[key]) cambios[key] = siguiente[key];
  });
  return cambios;
};

const modes = ["Lectura", "Solicitante", "Compras"];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  minWidth: "75%",
  maxWidth: "100vw",
  width: { xs: "100%", sm: "95%", md: "90%", lg: "70%", xl: "60%" },
  py: 3,
  pb: 0,
  px: 3,
  overflowY: "auto",
  overflowX: "hidden",
};

export default function AdministrarPedido({
  open,
  handleClose,
  refetchParent,
  id,
  mode: modeParent,
}) {
  const [fullscreen, setFullScreen] = useState(false);
  const [mode, setMode] = useState(modeParent);
  const { user, verificarAcceso } = useContext(UserContext);
  const { menu } = useContext(MenuContext);
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const [activeTab, setActiveTab] = useState(0);
  const TabComponents = [<Tab0 key={0} />, <Tab1 key={1} />, <Tab2 key={2} />, <Tab4 key={3} />];
  const [data, setCurrentData] = useState({});
  const [dataToSave, setDataToSave] = useState({});
  const [params, setParams] = useState({});
  // Pedido tal como se cargó de la base (null si es nuevo).
  const [original, setOriginal] = useState(null);
  // Los errores se muestran en un campo recién cuando el usuario lo modifica
  // o cuando intenta guardar.
  const [camposTocados, setCamposTocados] = useState({});
  const [intentoGuardar, setIntentoGuardar] = useState(false);

  const resetValidacion = () => {
    setCamposTocados({});
    setIntentoGuardar(false);
  };

  const [mutate, { loading: loadingMutation }] = useMutation(
    gql`
      mutation savePedidoCompraAdministrarPedido($input: PedidoCompraInput!) {
        savePedidoCompra(input: $input) {
          id
        }
      }
    `,
    {
      onCompleted: (data) => {
        handleSnackbar("Cambios guardados", "success");
        setDataToSave({});
        resetValidacion();
        refetch();
        refetchParent();
      },
      onError: (error) => {
        console.log(error);
        const errorValidacion = error?.graphQLErrors?.find(
          (e) => e?.extensions?.code === "BAD_USER_INPUT"
        );
        handleSnackbar(errorValidacion?.message || "Ha ocurrido un error, intente más tarde", "error");
      },
    }
  );

  const [getParams] = useLazyQuery(
    gql`
      query MontosPresupuesto {
        params(cod: "MONTOS_PRESUPUESTOS") {
          cod
          data
        }
      }
    `,
    {
      onCompleted: (data) => {
        let params = {};
        if (data) {
          data.params.forEach((param) => {
            params[param.cod] = param.data;
          });
          setParams(params);
        }
      },
    }
  );

  const [getPedido, { loading }] = useLazyQuery(
    gql`
      query getPedidoCompraAdministrarPedido($id: ID, $cancelados: Boolean) {
        pedidosCompra(filter: { id: $id }) {
          id
          nombre
          tipoCompra
          tipoDetalle
          numeroExpediente
          stockEstado
          stockObservaciones
          stockFecha
          subtotalEstimado
          updatedAt
          version
          estado
          estadoBase{
            nombre
            orden
          }
          stockUser {
            id
            nombre
            apellido
          }
          detalles {
            id
            nombreCompra
            unidadNombre
            unidadAbreviatura
            numero
            materiaPrima {
              id
              tipo
              nombre
              nombreCompra
              categoria
              precio
              tipoUnidad {
                nombre
                abreviatura
                multiplicador
              }
            }
            cantidad
            cantidadPack
            precioEstimado
            observacion
            nombreServicio
            cantidadVerificada
            observacionVerificacion
            file
            requierePack
            unidadPack
            totalMP
            equivalenciaPack { packs materiaPrima }
            provisionDetalle {
              precioUnitario
            }
            cotizacionSeleccionadaDesc
            cotizacionSeleccionada {
              precio
            }
            cotizaciones {
              id
            }
            rubroServicio
            productosFinales {
              id
              idProductoFinal
              cantidadProdFinal
              cantidadMP
              productoFinal { id nombre cantidadPorciones }
            }
          }
          estados(cancelados: $cancelados) {
            id
            base {
              id
              tipo
              orden
              nombre
            }
            user {
              id
              nombre
              apellido
            }
            file
            templateData
            finalizado
            updatedAt
          }
          licitacionInicio
          licitacionFin
          licitacionEstado
          provisiones {
            id
            presupuesto
            anulada
            motivoAnulacion
            totalAdjudicado
            totalRecibido
            createdAt
            fechaVencimiento
            estado
            enviado
            fechaEnvio
            usuarioQueEnvio {
              id
              nombre
              apellido
            }
            detalles {
              id
              precioUnitario
              cantidad
              cantidadEntregada
              idPedidoCompraDetalle
              materiaPrima {
                id
                nombre
                tipo
              }
              pedidoCompraDetalle {
                numero
                nombreServicio
              }
            }
            proveedor {
              id
              nombre
              apellido
              razonSocial
            }
            historial {
              evento
              motivo
              fecha
            }
            total
          }
          sucursal {
            id
            nombre
          }
          responsable {
            id
          }
  
          fechaSolicitud
          objetoCompra
          justificacion
          domicilioEntrega
          fechaEntrega
          firmante
          pliegoVencimientoDias
          pliegoRequiereDemanda
          pliegoParticularidades
          createdUser {
            id
            nombre
            apellido
          }
          cotizaciones{
            id
          }
        }
      }
    `,
    { fetchPolicy: "network-only" }
  );

  const refetch = () => {
    if (!id) return;
    getPedido({ variables: { id: id, cancelados: false } }).then(({ data }) => {
      if (data?.pedidosCompra?.[0]) {
        const pedido = { ...data?.pedidosCompra?.[0] };
        // Rehidratar fuentesPF desde productosFinales y definir ajusteManual
        if (Array.isArray(pedido.detalles)) {
          pedido.detalles = pedido.detalles.map((det) => {
            const fuentesPF = (det.productosFinales || []).map((pf) => ({
              idProducto: pf.idProductoFinal,
              nombreProducto: pf?.productoFinal?.nombre,
              cantidadPorciones: pf?.productoFinal?.cantidadPorciones,
              cantidadPF: pf.cantidadProdFinal,
              cantidadMP: pf.cantidadMP,
            }));
            const sumFuentes = fuentesPF.reduce((acc, f) => acc + (Number(f.cantidadMP) || 0), 0);
            const ajusteManual = (Number(det.cantidad) || 0) - sumFuentes;
            return {
              ...det,
              fuentesPF,
              ajusteManual,
            };
          });
        }
        // Normalizar responsable: asegurar idResponsable al hidratar desde DB
        if (!pedido.idResponsable && pedido?.responsable?.id) {
          pedido.idResponsable = pedido.responsable.id;
        }
        setCurrentData(pedido);
        setOriginal(pedido);
      }
    });
  };

  useEffect(() => {
    if (open) {
      getParams();
      if (id) {
        refetch();
      }
    } else {
      setCurrentData({});
      setDataToSave({});
      setOriginal(null);
      resetValidacion();
    }
  }, [open]);

  // Aviso de cambios no guardados al cerrar/recargar la pestaña
  useEffect(() => {
    const beforeUnloadHandler = (e) => {
      if (Object.keys(dataToSave).length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);
    return () => window.removeEventListener("beforeunload", beforeUnloadHandler);
  }, [dataToSave]);
  
  // Actualiza el pedido y registra en dataToSave solo los campos que cambiaron,
  // para no reenviar (y recrear en el backend) los detalles en cada guardado.
  const setData = (newData) => {
    setCurrentData((prev) => {
      const next = typeof newData === "function" ? newData(prev) : newData;
      const cambios = obtenerCambios(prev, next);
      if (Object.keys(cambios).length > 0) {
        setDataToSave((pendientes) => ({ ...pendientes, ...cambios }));
      }
      return next;
    });
  };

  const setDataWithoutSave = (newData) => {
    // Support both direct value and updater function
    if (typeof newData === "function") {
      setCurrentData((prev) => newData(prev));
    } else {
      setCurrentData(newData);
    }
    // No actualiza dataToSave para evitar marcar cambios pendientes
  };

  const close = () => {
    if (Object.keys(dataToSave).length > 0) {
      confirmar({
        title: "Cambios no guardados",
        message: "¿Desea cerrar sin guardar los cambios?",
        icon: "warning",
        func: () => {
          handleClose();
          setActiveTab(0);
        },
      });
    } else {
      handleClose();
      setActiveTab(0);
    }
  };

  const opcionesValidacion = useMemo(
    () => ({
      original,
      puedeElegirResponsable: verificarAcceso(PERMISOS_COMPRAS.ADMINISTRAR_EXPEDIENTES),
    }),
    [original, verificarAcceso]
  );

  const erroresValidacion = useMemo(
    () => validarPedido(data, opcionesValidacion),
    [data, opcionesValidacion]
  );

  const erroresPorCampo = useMemo(() => {
    const porCampo = {};
    erroresValidacion.forEach(({ campo, mensaje }) => {
      if (!porCampo[campo]) porCampo[campo] = mensaje;
    });
    return porCampo;
  }, [erroresValidacion]);

  // Mensaje de error de un campo, solo si ya corresponde mostrarlo.
  const getError = useCallback(
    (campo) => (intentoGuardar || camposTocados[campo] ? erroresPorCampo[campo] || null : null),
    [intentoGuardar, camposTocados, erroresPorCampo]
  );

  const marcarTocado = useCallback((campo) => {
    setCamposTocados((prev) => (prev[campo] ? prev : { ...prev, [campo]: true }));
  }, []);

  const mostrarErrores = (errores, prefijo = "") => {
    setIntentoGuardar(true);
    setActiveTab(errores[0].tab);
    handleSnackbar(`${prefijo}${errores[0].mensaje}`, "error");
  };

  const guardar = async (datos, cambios) => {
    const tipoDetalle = cambios.tipoDetalle || datos.tipoDetalle;

    const input = {};
    CAMPOS_PEDIDO_EDITABLES.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(cambios, key)) {
        input[key] = cambios[key];
      }
    });

    if (input.stockUser && typeof input.stockUser === 'object') {
      input.stockUser = input.stockUser.id ?? null;
    }
    // Excluir detalles marcados para eliminación (_toRemove)
    if (input.detalles && Array.isArray(input.detalles)) {
      input.detalles = input.detalles.filter(d => !d._toRemove);
    }

    // Los ítems ya guardados se envían con su id para que el backend los actualice en lugar de recrearlos
    const idPersistido = (id) => (id != null && !String(id).startsWith("temp_") ? id : undefined);

    if (input.detalles && tipoDetalle == "Productos") {
      input.detalles = input.detalles.map((detalle) => {
        return {
          id: idPersistido(detalle.id),
          idMateriaPrima: detalle.materiaPrima.id,
          // cantidad: detalle.cantidad * detalle.materiaPrima.tipoUnidad.multiplicador,
          cantidad: Number(detalle.cantidad),
          precioEstimado: detalle.precioEstimado,
          observacion: detalle.observacion,
          file: detalle.file,
          fileUpload: detalle.fileUpload,
          requierePack: detalle.requierePack,
          unidadPack: detalle.unidadPack,
          cantidadPack: Number(detalle.cantidadPack),
          totalMP: Number(detalle.totalMP),
          nombreCompra: nombreCompraDetalle(detalle),
          cantidadVerificada: detalle.cantidadVerificada ?? null,
          // Persistir fuentes de Productos Finales
          productosFinales: Array.isArray(detalle.fuentesPF)
            ? detalle.fuentesPF.map((f) => ({
                idProductoFinal: f.idProducto,
                cantidadProdFinal: f.cantidadPF ?? 1,
                cantidadMP: Number(f.cantidadMP) ?? 0,
              }))
            : [],
        };
      });
    }
    if (input.detalles && tipoDetalle == "Servicios") {
      input.detalles = input.detalles.map((detalle) => {
        return {
          id: idPersistido(detalle.id),
          nombreServicio: detalle.nombreServicio,
          cantidad: detalle.cantidad ?? 1,
          cantidadVerificada: detalle.cantidadVerificada ?? 1,
          precioEstimado: detalle.precioEstimado,
          observacion: detalle.observacion,
          rubroServicio: detalle.rubroServicio,
          file: detalle.file,
          fileUpload: detalle.fileUpload,
        };
      });
    }

    // Normalizar campos de Pliego Particular
    if (input.hasOwnProperty('pliegoVencimientoDias')) {
      if (input.pliegoVencimientoDias === '' || input.pliegoVencimientoDias === null || input.pliegoVencimientoDias === undefined) {
        input.pliegoVencimientoDias = null; // opcional
      } else {
        const n = parseInt(input.pliegoVencimientoDias, 10);
        input.pliegoVencimientoDias = Number.isNaN(n) ? null : n;
      }
    }

    if (id) {
      input.id = id;
    }

    await mutate({ variables: { input: input } });

    if (!id) {
      handleClose();
    }
  };

  // Cambia el tipo de compra al que corresponde según el monto y guarda si el pedido queda válido.
  const aplicarTipoCompra = (tipoCompra) => {
    const cambiosTipo = {
      tipoCompra,
      // Igual que al cambiar el tipo manualmente: el pliego depende del tipo de compra.
      pliegoVencimientoDias: null,
      pliegoRequiereDemanda: null,
      pliegoParticularidades: null,
    };
    const nuevosDatos = { ...data, ...cambiosTipo };
    const nuevosCambios = { ...dataToSave, ...cambiosTipo };
    setCurrentData(nuevosDatos);
    setDataToSave(nuevosCambios);
    marcarTocado("tipoCompra");

    const errores = validarPedido(nuevosDatos, opcionesValidacion);
    if (errores.length > 0) {
      mostrarErrores(errores, `Tipo de compra cambiado a ${TIPOS_COMPRA[tipoCompra]?.etiqueta ?? tipoCompra}. `);
      return;
    }
    guardar(nuevosDatos, nuevosCambios);
  };

  const confirmarTipoCompraSegunMonto = ({ total, tipoCorrecto }) => {
    const montos = params?.MONTOS_PRESUPUESTOS;
    const etiqueta = (tipo) => TIPOS_COMPRA[tipo]?.etiqueta ?? tipo;
    const seguirEditando = () => setActiveTab(1);

    const mensaje = (
      <SoftBox textAlign="left">
        <SoftTypography variant="button" fontWeight="regular" component="p">
          El total estimado del pedido es <b>${formatMoneyPunto(total)}</b>.
        </SoftTypography>
        <SoftTypography variant="button" fontWeight="regular" component="p">
          Tipo seleccionado: <b>{etiqueta(data.tipoCompra)}</b> (
          {describirIntervalo(data.tipoCompra, montos)}).
        </SoftTypography>
        <SoftTypography variant="button" fontWeight="regular" component="p">
          Según los montos establecidos corresponde: <b>{etiqueta(tipoCorrecto)}</b> (
          {describirIntervalo(tipoCorrecto, montos)}).
        </SoftTypography>
      </SoftBox>
    );

    confirmar({
      icon: "warning",
      title: "Tipo de compra según monto",
      message: mensaje,
      confirmLabel: `Cambiar a ${etiqueta(tipoCorrecto)}`,
      cancelLabel: "Seguir editando",
      func: () => aplicarTipoCompra(tipoCorrecto),
      cancelFunc: seguirEditando,
    });
  };

  const handleSave = async () => {
    if (erroresValidacion.length > 0) {
      mostrarErrores(erroresValidacion);
      return;
    }

    // El tipo de compra debe corresponder al total estimado mientras todavía pueda cambiarse.
    const tipoCompraModificable = puedeCambiarTipoCompra(data, {
      puedeEditar: puedeEditarDatosGenerales(data, mode, user),
      puedeCrearSolicitud: verificarAcceso(PERMISOS_CREAR_SOLICITUD, "or"),
    });
    if (tipoCompraModificable) {
      const desvio = verificarTipoCompraSegunMonto(
        data.tipoCompra,
        calcularTotalEstimado(data.detalles, data.tipoDetalle),
        params?.MONTOS_PRESUPUESTOS
      );
      if (desvio) {
        confirmarTipoCompraSegunMonto(desvio);
        return;
      }
    }

    await guardar(data, dataToSave);
  };
    const changeMode = useCallback(
    (e) => {
      e.stopPropagation();
      if (!user || (user.tipoUser !== "Administrador")) return;
      menu({
        open: e.currentTarget,
        align: "left",
        options: [
          {
            name: "Original",
            onClick: () => {
              setMode(modeParent);
            },
            disabled: modeParent === mode,
            icon: modeParent === mode ? { icon: "replay", color: "secondary" } : undefined,
          },
          ...modes.map((m) => ({
            name: m,
            onClick: () => {
              setMode(m);
            },
            disabled: m === mode,
            icon: m === mode ? { icon: "check", color: "success" } : undefined,
          })),
        ],
      });
    },
    [user, mode, modeParent, menu]
  );

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={close}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={open}>
          <Card
            sx={{
              ...style,
              boxSizing: "border-box",
              ...(fullscreen
                ? {
                    width: "100%",
                    height: "100%",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    overflowX: "hidden",
                  }
                : {}),
            }}
          >
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftBox display="flex" alignItems="center" gap={1}>
                {loading ? <CircularProgress size={20} /> : <Icon fontSize="md">checklist</Icon>}
                <SoftTypography variant="h6" fontWeight="bold">
                  Pedido {data.id ? `#${data.id}` : ""}
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="end" alignItems="center" gap={1}>
                {mode !== "Lectura" && (
                  <Tooltip
                    placement="top"
                    title={
                      Object.keys(dataToSave).length == 0
                        ? "No hay cambios para guardar"
                        : intentoGuardar && erroresValidacion.length > 0
                        ? erroresValidacion[0].mensaje
                        : "Guardar cambios"
                    }
                  >
                    <span>
                      <SoftButton
                        variant="gradient"
                        color="success"
                        iconOnly
                        circular
                        disabled={loading || loadingMutation || Object.keys(dataToSave).length == 0}
                        onClick={handleSave}
                      >
                        {loadingMutation ? (
                          <CircularProgress color="white" size={17} />
                        ) : (
                          <Icon sx={{ fontWeight: "regular" }}>save</Icon>
                        )}
                      </SoftButton>
                    </span>
                  </Tooltip>
                )}

                <Tooltip placement="top" title={fullscreen ? "Minimizar" : "Maximizar"}>
                  <SoftButton
                    variant="gradient"
                    color="uproYellow"
                    iconOnly
                    circular
                    onClick={() => {
                      setFullScreen(!fullscreen);
                    }}
                  >
                    <Icon sx={{ fontWeight: "regular" }}>
                      {fullscreen ? "fullscreen_exit" : "fullscreen"}
                    </Icon>
                  </SoftButton>
                </Tooltip>

                <Tooltip placement="top" title="Cerrar">
                  <SoftButton variant="gradient" color="error" iconOnly circular onClick={close}>
                    <Icon sx={{ fontWeight: "regular" }}>close</Icon>
                  </SoftButton>
                </Tooltip>
              </SoftBox>
            </SoftBox>
            <SoftBox py={1} sx={{ overflowX: "hidden" }}>
              <Tabs
                value={activeTab}
                onChange={(e, value) => setActiveTab(value)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{ maxWidth: "100%" }}
              >
                <Tab
                  label={
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      justifyContent="center"
                    >
                      <Icon fontSize="small" sx={{ color: getUproColor("uproYellow") }}>
                        text_snippet
                      </Icon>
                      Datos generales
                    </SoftBox>
                  }
                />
                <Tab
                  label={
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      justifyContent="center"
                    >
                      <Icon fontSize="small" sx={{ color: getUproColor("uproYellow") }}>
                        category
                      </Icon>
                      Detalles
                    </SoftBox>
                  }
                />
                <Tab
                  label={
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      justifyContent="center"
                    >
                      <Icon fontSize="small" sx={{ color: getUproColor("uproYellow") }}>
                        route
                      </Icon>
                      Estados
                    </SoftBox>
                  }
                  sx={{ display: !id ? "none" : "block" }}
                />
                <Tab
                  label={
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      justifyContent="center"
                    >
                      <Icon fontSize="small" sx={{ color: getUproColor("uproYellow") }}>
                        format_list_numbered
                      </Icon>
                      Ordenes
                    </SoftBox>
                  }
                  sx={{ display: !id ? "none" : "block" }}
                />
              </Tabs>
            </SoftBox>
            <div
              style={{
                transition: "height 0.3s ease",
                overflow: "hidden",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <SoftBox sx={{ overflowY: "auto", overflowX: "hidden", minWidth: 0 }}>
                <AdministrarContext.Provider
                  value={{
                    mode,
                    data,
                    setData,
                    setDataWithoutSave,
                    getError,
                    marcarTocado,
                    handleSnackbar,
                    mutate,
                    refetch,
                    params,
                    refetchParent,
                    dataToSave,
                    loading,
                    handleSave,
                    fullscreen,
                  }}
                >
                  {TabComponents[activeTab]}
                </AdministrarContext.Provider>
              </SoftBox>
            </div>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftTypography
                variant="caption"
                fontWeight="light"
                color="secondary"
                py={1}
                onClick={changeMode}
              >
                Modo {mode ?? "Lectura"}
              </SoftTypography>
              <SoftTypography variant="caption" fontWeight="light" color="secondary" py={1}>
                {formatDate(data?.updatedAt, "Modificado por última vez el dd/MM/yyyy a las hh:mm")}
              </SoftTypography>
            </SoftBox>
          </Card>
        </Fade>
      </Modal>
    </>
  );
}

AdministrarPedido.defaultProps = {
  mode: "Lectura",
};

AdministrarPedido.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetchParent: PropTypes.func,
  id: PropTypes.string,
  mode: PropTypes.oneOf(modes),
};
